import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useFocusNext } from "../../../hooks/useFocusNext";
import { Row } from "react-grid-system";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import { ViewOffIcon, ViewOnIcon } from "../../atoms/icons/ViewIcons";
import IconButton from "../../atoms/buttons/IconButton";
import { ClearIcon } from "../../atoms/icons/ClearIcon";
import Divider from "../../atoms/Divider";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import { AddIcon } from "../../atoms/icons/AddIcon";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Alert from "../../molecules/Alert";
import { CircleActivityIndicator } from "../../atoms/CircleActivityIndicator";
import Checkbox from "../../atoms/inputs/Checkbox";
import type { CustomHighlight } from "../PdfTemplate";
import type {
  PossibleFieldPositionWithValue,
  StampInformation,
} from "../../../models/ClientInvoiceReaderTemplateModel";
import type { Position } from "../../../models/Position";
import type { TemplateTabHandle } from "../../../models/Templates";
import { useUpdateTemplateStamp } from "../../../graphql/hooks/invoiceInterpretationTemplates/useUpdateTemplateStamp";
import {
  resolvePositionWithValue,
  alternativeStampsColorMap,
  positionKey,
  toTemplateStampInput,
  buildFieldOptions,
  findPageForPosition,
} from "../../../utils/templateHelpers";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import AutocompleteSelect from "../../atoms/inputs/AutocompleteSelect";
import ToggleAllTextButton from "../../atoms/buttons/ToggleAllTextButton";
import ActiveFieldWrapper from "./ActiveFieldWrapper";

const FlexedDiv = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const FlexedDivCentered = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

interface Props {
  highlightedFields: CustomHighlight[];
  setHighlightedFields: React.Dispatch<React.SetStateAction<CustomHighlight[]>>;
  possibleFields: PossibleFieldPositionWithValue[];
  stampInformation?: StampInformation;
  onSaveAndContinue: () => void;
  setStampInformation?: React.Dispatch<
    React.SetStateAction<StampInformation | undefined>
  >;
  onDirtyChange?: () => void;
  onNavigateToPage?: (page: number) => void;
  totalPages?: number;
  onSaveError?: (message: string) => void;
}

const EditStamps = forwardRef<TemplateTabHandle, Props>(
  (
    {
      highlightedFields,
      setHighlightedFields,
      possibleFields,
      stampInformation,
      onSaveAndContinue,
      setStampInformation,
      onDirtyChange,
      onNavigateToPage,
      totalPages,
      onSaveError,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { id: clientIdentifier, templateId } = useParams();
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    const navigateAfterSaveRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [isLabelActive, setIsLabelActive] = useState(false);
    const { formRef, lastFocusedInputRef, focusNextInput } = useFocusNext();
    const [stampLabelPageNumber, setStampLabelPageNumber] = useState<number | undefined>(
      () => stampInformation?.label?.position
        ? findPageForPosition(stampInformation.label.position, possibleFields)
        : undefined,
    );

    const defaultPosition = {
      bottomLeftX: 0,
      bottomLeftY: 0,
      topRightX: 360,
      topRightY: 60,
    };

    useEffect(() => {
      if (
        setStampInformation &&
        stampInformation &&
        !stampInformation.position
      ) {
        setStampInformation((prev) =>
          prev && !prev.position
            ? { ...prev, position: defaultPosition }
            : prev,
        );
      }
      // defaultPosition is intentionally excluded: it is a stable inline constant,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stampInformation, setStampInformation]);

    const markDirty = (isDirty: boolean) => {
      dirtyRef.current = isDirty;
      setDirty(isDirty);
      onDirtyChange?.();
    };

    const { updateTemplateStamp, loading } = useUpdateTemplateStamp({
      onError: (e) => {
        const msg = e?.message || t("errors.somethingWentWrong");
        setError(msg);
        onSaveError?.(msg);
      },
      onCompleted: (result) => {
        if (result.invoiceInterpretation?.updateTemplateStamp?.success) {
          if (navigateAfterSaveRef.current) { onSaveAndContinue(); }
        } else {
          setError(t("errors.somethingWentWrong"));
        }
      },
    });

    const onToggleHighlightValue = (value?: string, position?: Position, pageNumber?: number) => {
      if (!value || !position) return;
      const existing = highlightedFields.find((h) => h.id === value);
      if (existing) {
        setHighlightedFields((prev) => prev.filter((h) => h.id !== value));
      } else {
        setHighlightedFields((prev) => [
          ...prev,
          {
            id: value,
            label: value,
            position,
            pageNumber,
            color: "#4FAE9D",
            background: "rgba(174, 255, 80, 0.15)",
          },
        ]);
      }
    };

    const addAlternativeStamp = () => {
      if (!setStampInformation || !stampInformation?.position) return;

      setStampInformation((prev) => {
        if (!prev || !prev.position) return prev;
        const currentAlts = prev.alternativeStampPositions ?? [];
        const newAltStamps = [...currentAlts, { ...prev.position }];
        return { ...prev, alternativeStampPositions: newAltStamps };
      });

      markDirty(true);
    };

    const removeAlternativeStamp = (index: number) => {
      if (!setStampInformation || !stampInformation) return;

      const removed = stampInformation.alternativeStampPositions[index];
      const id = `altStamp-${positionKey(removed)}`;

      setHighlightedFields((prev) => prev.filter((h) => h.id !== id));

      setStampInformation((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          alternativeStampPositions: prev.alternativeStampPositions.filter(
            (_, i) => i !== index,
          ),
        };
      });

      markDirty(true);
    };

    const areAllHighlighted = () => {
      // If no stamps have positions, return false
      const hasMainStampPosition = stampInformation?.label?.position;
      const hasAltStamps =
        (stampInformation?.alternativeStampPositions?.length ?? 0) > 0;

      if (!hasMainStampPosition && !hasAltStamps) return false;

      // Check main stamp if it has a position
      const mainHighlighted = hasMainStampPosition
        ? !!highlightedFields.find(
            (h) => h.id === stampInformation.label?.value,
          )
        : true;

      // Check alternative stamps
      const altsHighlighted =
        stampInformation?.alternativeStampPositions?.every((pos) => {
          const id = `altStamp-${positionKey(pos)}`;
          return highlightedFields.some((h) => h.id === id);
        }) ?? true;

      return mainHighlighted && altsHighlighted;
    };

    const toggleAll = () => {
      if (!stampInformation) return;

      if (areAllHighlighted()) {
        // Hide all - remove main stamp and all alternative stamps
        const idsToRemove = [
          stampInformation.label?.value,
          ...stampInformation.alternativeStampPositions.map(
            (pos) => `altStamp-${positionKey(pos)}`,
          ),
        ].filter(Boolean);
        setHighlightedFields((prev) =>
          prev.filter((h) => !idsToRemove.includes(h.id)),
        );
      } else {
        // Show all - add main stamp and all alternative stamps
        const newHighlights: CustomHighlight[] = [];

        // Main stamp
        if (stampInformation.label?.value && stampInformation.label.position) {
          newHighlights.push({
            id: stampInformation.label.value,
            label: stampInformation.label.value,
            position: stampInformation.label.position,
            pageNumber: stampLabelPageNumber,
            color: "#4FAE9D",
            background: "rgba(174, 255, 80, 0.15)",
          });
        }

        // Alternative stamps
        stampInformation.alternativeStampPositions.forEach((pos, index) => {
          const id = `altStamp-${positionKey(pos)}`;
          newHighlights.push({
            id,
            label: id,
            position: pos,
            color: alternativeStampsColorMap[index],
            background: "rgba(174, 255, 80, 0.15)",
          });
        });

        // Remove existing stamp highlights and add new ones
        const idsToRemove = [
          stampInformation.label?.value,
          ...stampInformation.alternativeStampPositions.map(
            (pos) => `altStamp-${positionKey(pos)}`,
          ),
        ].filter(Boolean);
        const filtered = highlightedFields.filter(
          (h) => !idsToRemove.includes(h.id),
        );
        setHighlightedFields([...filtered, ...newHighlights]);
      }
    };

    const save = async () => {
      if (!dirtyRef.current) return;
      setError(null);
      if (!clientIdentifier || !templateId) {
        setError("Missing template identifiers");
        return;
      }
      const stamp = toTemplateStampInput(stampInformation);
      if (!stamp) return;
      await updateTemplateStamp({ stamp, clientIdentifier, templateId });
      markDirty(false);
    };

    useImperativeHandle(ref, () => ({
      save,
      isDirty: () => dirtyRef.current,
      markDirty: () => markDirty(true),
      handleFieldClick: (clickedField) => {
        if (!clickedField.position || !clickedField.value) return;
        setStampInformation?.((prev) =>
          prev
            ? { ...prev, label: { value: clickedField.value!, position: clickedField.position! }, stampAllPages: false }
            : prev,
        );
        setStampLabelPageNumber(clickedField.pageNumber);
        setHighlightedFields((prev) => [
          ...prev.filter((h) => h.id !== clickedField.value),
          { id: clickedField.value!, label: clickedField.value!, position: clickedField.position!, pageNumber: clickedField.pageNumber, color: "#4FAE9D", background: "rgba(174, 255, 80, 0.15)" },
        ]);
        setIsLabelActive(false);
        markDirty(true);
        onNavigateToPage?.(clickedField.pageNumber);
        focusNextInput();
      },
    }));

    const onClickSaveAndContinue = async () => {
      navigateAfterSaveRef.current = true;
      try {
        await save();
      } catch (e: any) {
        setError(e.message || t("errors.somethingWentWrong"));
      } finally {
        navigateAfterSaveRef.current = false;
      }
    };

    return (
      <div ref={formRef}>
        {error && <Alert type="error" title={error} />}
        {!stampInformation?.position ? (
          <OutlinedButton
            medium
            title={t("templates.addStamp")}
            icon={<AddIcon color={theme.colors.primary} />}
            color={theme.colors.primary}
          />
        ) : (
          <div>
            {stampInformation?.label && (
              <>
                <HorizontalSpacer spacing={1} />
                <Row nogutter justify="end">
                  <ToggleAllTextButton
                    onClick={toggleAll}
                    showHideAll={areAllHighlighted()}
                    showAllText={t("templates.showAll")}
                    hideAllText={t("templates.hideAll")}
                  />
                </Row>
                <HorizontalSpacer spacing={1} />
              </>
            )}
            {/* Main stamp */}
            <Row nogutter justify="between" align="center">
              <FlexedDiv>
                <CircleActivityIndicator color={theme.colors.text} />
                <Body>{t("templates.mainStamp")}</Body>
              </FlexedDiv>
              <ToggleButton
                onToggle={() =>
                  onToggleHighlightValue(
                    stampInformation?.label?.value,
                    stampInformation?.label?.position,
                    stampLabelPageNumber,
                  )
                }
                isOn={
                  !!highlightedFields.find(
                    (h) => h.id === stampInformation?.label?.value,
                  )
                }
                size="medium"
                iconOn={<ViewOnIcon color={theme.colors.white} />}
                iconOff={<ViewOffIcon color={theme.colors.primary} />}
                tabIndex={-1}
              />
            </Row>
            <HorizontalSpacer spacing={1} />

            <BodySmall onClick={() => setIsLabelActive(true)} style={{ cursor: "pointer" }}>{t("templates.label")}</BodySmall>
            <FlexedDivCentered>
              <ActiveFieldWrapper $active={isLabelActive} onClick={() => setIsLabelActive(true)} onFocus={(e) => { setIsLabelActive(true); if (e.target instanceof HTMLInputElement) lastFocusedInputRef.current = e.target; }}>
                <AutocompleteSelect
                  value={stampInformation?.label?.value || ""}
                  onChange={(value) => {
                    const resolved = resolvePositionWithValue(
                      value,
                      possibleFields,
                    );

                    setStampInformation?.((prev) =>
                      prev ? { ...prev, label: resolved, stampAllPages: false } : prev,
                    );

                    markDirty(true);

                    if (resolved) {
                      setStampLabelPageNumber(resolved.pageNumber);
                      setHighlightedFields((prev) => [
                        ...prev.filter((h) => h.id !== resolved.value),
                        { id: resolved.value, label: resolved.value, position: resolved.position, pageNumber: resolved.pageNumber, color: "#4FAE9D", background: "rgba(174, 255, 80, 0.15)" },
                      ]);
                      onNavigateToPage?.(resolved.pageNumber);
                    } else {
                      setStampLabelPageNumber(undefined);
                    }
                    focusNextInput();
                  }}
                  options={buildFieldOptions(possibleFields, totalPages, t("common.page"), t("templates.selectAField"))}
                />
              </ActiveFieldWrapper>
              <IconButton
                icon={<ClearIcon />}
                onClick={() =>
                  setStampInformation?.((prev) =>
                    prev ? { ...prev, label: undefined, stampAllPages: false } : prev,
                  )
                }
                tabIndex={-1}
              />
            </FlexedDivCentered>
            <HorizontalSpacer spacing={0.25} />
            <Checkbox
              small
              title={t("templates.stampHasNoLabel")}
              checked={stampInformation?.stampAllPages ?? false}
              onChange={(checked: boolean) =>
                setStampInformation?.((prev) =>
                  prev
                    ? { ...prev, stampAllPages: checked, label: checked ? undefined : prev.label }
                    : prev,
                )
              }
            />

            {stampInformation?.alternativeStampPositions.map((_, index) => (
              <Row key={index} nogutter justify="between" align="center">
                <HorizontalSpacer spacing={1} />
                <FlexedDiv>
                  <CircleActivityIndicator
                    color={alternativeStampsColorMap[index]}
                  />
                  <Body>
                    {t("templates.alternativeStamp")} {index + 1}
                  </Body>
                </FlexedDiv>
                <div style={{ display: "flex", gap: 12 }}>
                  <IconButton
                    icon={<TrashIcon color={theme.colors.error} />}
                    color={theme.colors.error20}
                    onClick={() => removeAlternativeStamp(index)}
                  />
                </div>
              </Row>
            ))}

            <HorizontalSpacer spacing={2} />
            <OutlinedButton
              title="Lägg till alternativ stämpel"
              medium
              color={theme.colors.primary}
              icon={<AddIcon color={theme.colors.primary} />}
              onClick={addAlternativeStamp}
            />

            <HorizontalSpacer spacing={0.5} />
            <BodySmall color={theme.colors.textSecondary}>
              {t("templates.alternativeStampDescription")}
            </BodySmall>
          </div>
        )}

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />
        <Row nogutter justify="end">
          <OutlinedButton
            title={t("random.saveAndContinue")}
            iconRight={<ChevronSmallRightIcon color={theme.colors.primary} />}
            color={theme.colors.primary}
            medium
            onClick={onClickSaveAndContinue}
            disabled={loading || !dirty}
          />
        </Row>
      </div>
    );
  },
);

export default EditStamps;
